/**
 * `WidgetApp` — the widget's launcher + panel state machine (ARCHITECTURE §7).
 *
 * Boot sequence:
 *   parseEmbed → loadWidgetConfig → applyWidgetTheme → (resume | prechat)
 *   → chatting over WS → offline / ended.
 *
 * The visitor state lives in `visitorStore` (bound via `useVisitorSession`); the
 * WS connection is owned by `useWidgetSocket`. This component only decides which
 * screen to render for the current `phase`, and drives the loader's iframe
 * expand/collapse via `postMessage`.
 *
 * Skeleton-grade: real wiring + screen switch; network calls are `TODO(phase 3)`
 * stubs inside the hooks, so it renders end-to-end without a backend.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { parseEmbed, type EmbedContext } from './config/parseEmbed';
import { widgetBrand } from './config/brand';
import { subscribeCobrowse, isCobrowsing, visitorStopCobrowse } from './cobrowse/cobrowse';
import { signalVisitorTyping, stopVisitorTyping } from './realtime/typing';
import { useQueuePosition, formatWait } from './session/useQueuePosition';
import { loadWidgetConfig, getWidgetApi, type WidgetBootstrap } from './config/loadWidgetConfig';
import { isMuted, setMuted, playChime, setSoundEnabled } from './sound';
import { useVisitorCall } from './voice/useVisitorCall';
import { useTriggers } from './config/useTriggers';
import { applyWidgetTheme } from './theme/applyWidgetTheme';
import { useVisitorPresence } from './presence/useVisitorPresence';
import { useVisitorSession } from './session/useVisitorSession';
import { visitorStore, readLauncherDismissed, writeLauncherDismissed } from './session/visitorStore';
import { postToLoader, subscribeLoaderSupport, getHideSupport } from './config/loaderBridge';
import { useWidgetSocket } from './realtime/useWidgetSocket';
import { LauncherBubble, LauncherRestoreArrow } from './screens/LauncherBubble';
import { PreChatForm } from './screens/PreChatForm';
import { ChatScreen } from './screens/ChatScreen';
import { OfflineScreen } from './screens/OfflineScreen';
import { EndedScreen } from './screens/EndedScreen';

export function WidgetApp() {
  // Resolve the embed identity once (from `?cid=` on the iframe URL).
  const embed = useMemo<EmbedContext | null>(() => {
    try {
      return parseEmbed();
    } catch {
      return null;
    }
  }, []);

  const [config, setConfig] = useState<WidgetBootstrap | null>(null);
  // A proactive greeting surfaced by a matched trigger (shown atop the pre-chat form).
  const [proactive, setProactive] = useState<string | null>(null);
  // Header ⋮ menu: Sound toggle (persisted mute pref) + End chat (visitor-initiated).
  const [menuOpen, setMenuOpen] = useState(false);
  const [muted, setMutedState] = useState<boolean>(() => isMuted());
  // Launcher greeting teaser ("show messages on load") — dismissable, one per load.
  const [teaserDismissed, setTeaserDismissed] = useState(false);
  // One-shot guard so auto-open fires at most once (never re-opens after the visitor closes).
  const autoOpenedRef = useRef(false);
  // The visitor put the launcher away for this browsing session. Read synchronously (same
  // pattern as `muted` above) so a dismissed launcher never flashes on the next page load
  // before the effect below can tell the loader to hide the frame.
  const [launcherDismissed, setLauncherDismissed] = useState<boolean>(() =>
    embed ? readLauncherDismissed(embed.cid) : false,
  );
  // Mirror for callbacks that fire outside the render owning the state (auto-open timers,
  // message handlers). Seeded here, then kept in step with `frameDocked` by the two mutators
  // below and by the effect that watches the handshake.
  const dismissedRef = useRef(launcherDismissed);

  // Launcher show-delay (`theme.launcherDelaySeconds`): the org can hold the icon back for the
  // first N seconds of a page view — page loads clean, the widget appears once the visitor has
  // settled. Until the reveal, this app renders nothing and posts nothing, so the iframe keeps
  // its boot-time hidden state. Two things outrank the timer (owner's ruling): an ONGOING
  // conversation (a session token exists — resumed chat or rating screen) and the panel being
  // open (an agent engaging auto-opens it) — a live conversation is never hidden behind a
  // marketing timer. The auto-open/proactive timers keep counting from page load; one that
  // fires mid-delay is HELD and applied at reveal, so the panel can never precede the icon.
  const [launcherRevealed, setLauncherRevealed] = useState(false);
  const concealedRef = useRef(false);
  const pendingAutoOpenRef = useRef(false);

  // Can the loader on the other side of this frame actually take it off the host page?
  // Subscribed, not read once: the answer arrives by postMessage a beat after mount, and a
  // launcher that decided at mount would never grow its × (see `loaderBridge`).
  const hideSupport = useSyncExternalStore(subscribeLoaderSupport, getHideSupport, getHideSupport);
  // Only offer the × when we KNOW the frame can leave. Against a stale cached loader this
  // stays false forever and the feature is simply absent — the one degradation that cannot
  // leave an invisible click-eating box on a customer's site.
  const canDismiss = hideSupport === 'yes';
  // A stored dismissal is honored on the same terms. If this visitor dismissed under a newer
  // loader and then met an older one (a rollback, a second cached copy), the flag goes inert
  // and the launcher comes back rather than vanishing into an unclickable frame.
  const dismissed = launcherDismissed && canDismiss;
  // Whether we are asking the loader to shrink the frame to the restore arrow — BROADER than
  // `dismissed`, because it also covers the handshake still being in flight. A dismissed
  // visitor gets `hide` posted straight away, before we know who is listening: a new loader
  // honors it (no launcher flash on page load), an old loader ignores it and keeps showing the
  // frame — which is why the render below still puts a working launcher inside it. Neither
  // side of that race leaves a dead zone.
  //
  // This is also the flag the rest of the widget reasons about, so "the frame is away" and
  // "suppress the marketing nudges / don't widen for a teaser" can never disagree. Once the
  // handshake settles on `no` it goes false for good and a stored dismissal is fully inert.
  const frameDocked = dismissed || (launcherDismissed && hideSupport === 'unknown');

  const toggleMuted = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
  };

  const endChat = async () => {
    setMenuOpen(false);
    const sessionId = visitorStore.getSnapshot().session?.id;
    if (embed && sessionId) {
      try {
        await getWidgetApi().widget.leave(embed.cid, sessionId);
      } catch {
        /* best-effort — still move the visitor to the ended/CSAT screen */
      }
    }
    visitorStore.setPhase('ended');
  };

  const { state, setOpen, startSession, resumeSession, sendMessage, sendAttachment } = useVisitorSession(
    embed ?? ({ cid: '', orgId: '', widgetId: '', context: null, pageUrl: null } as EmbedContext),
    config,
  );

  /** Visitor tapped the × on the launcher — put it away for this browsing session. */
  const dismissLauncher = () => {
    if (embed) writeLauncherDismissed(embed.cid, true);
    dismissedRef.current = true;
    setLauncherDismissed(true);
    // The × takes the whole collapsed cluster away, teaser included — so don't let a
    // stale greeting pop back beside the launcher when it is restored later.
    setTeaserDismissed(true);
  };

  /** Bring a dismissed launcher back (and forget the dismissal). */
  const restoreLauncher = useCallback(() => {
    if (embed) writeLauncherDismissed(embed.cid, false);
    dismissedRef.current = false;
    setLauncherDismissed(false);
  }, [embed]);

  /**
   * Open the panel from a LIVE path — an agent engaging from the Visitors panel, or
   * co-browse starting. These always win over a dismissal: the visitor is already in a
   * real interaction, and leaving the launcher hidden would strand it. Restoring here
   * also means closing the panel afterwards lands on a normal launcher rather than
   * silently hiding the conversation again.
   */
  const openPanel = useCallback(() => {
    restoreLauncher();
    setOpen(true);
  }, [restoreLauncher, setOpen]);

  /**
   * Open the panel from an AUTOMATIC path — the org's "open on load" delay or a proactive
   * trigger. These are marketing nudges, so a visitor who just said "go away" wins; the
   * one-shot guards still burn, exactly as they do when the nudge is honored.
   */
  const autoOpenPanel = useCallback(() => {
    if (dismissedRef.current) return;
    if (concealedRef.current) {
      // Show-delay still running: hold the open and apply it at reveal — the org's auto-open
      // clock keeps counting from page load, but the panel can never precede the icon.
      pendingAutoOpenRef.current = true;
      return;
    }
    setOpen(true);
  }, [setOpen]);

  const connectionState = useWidgetSocket(state.sessionToken, embed?.cid);

  const theme = config?.theme;

  // Document title follows the org's own widget name (Settings → Widget → "Header name"),
  // falling back to the brand-neutral default. Set at RUNTIME rather than in index.html
  // because that document is served before the app knows which org it is — the tenant is
  // only known once `?cid=` is parsed and the public config resolves.
  useEffect(() => {
    document.title = theme?.headerName?.trim() || widgetBrand.frameTitle;
  }, [theme?.headerName]);

  // Concealed while the show-delay runs — and, crucially, while the config is still LOADING:
  // before the config arrives the delay is unknown, and the first cut of this gated concealment
  // on `!!config`, which meant the app posted `collapse` at ~0.5s, showed the launcher, and then
  // went dark when a configured delay kicked in — leaving an invisible, click-eating 96px frame
  // over the host page for the rest of the delay (caught by the postMessage trace in
  // verification: `collapse` at t=0.47s, config not yet loaded). Concealed-by-default flips the
  // failure mode: nothing exists until the delay is KNOWN to be zero/elapsed. A config fetch
  // error reveals immediately (the frame must be able to show the error state), and the session
  // token / open panel still bypass everything — live conversations outrank the timer.
  const launcherConcealed =
    !launcherRevealed &&
    !state.open &&
    !state.sessionToken &&
    state.phase !== 'error' &&
    (config == null || (theme?.launcherDelaySeconds ?? 0) > 0);
  // Org-level sound gate hides the per-visitor toggle when the org disabled sound entirely.
  const soundOn = theme?.messageSound !== false;
  // Greeting teaser beside the collapsed launcher ("show messages on load"). Gated on
  // `frameDocked`, not `dismissed`: while the handshake is unresolved we are posting `dock`
  // rather than `collapse`, so nobody has widened the collapsed frame and a teaser rendered
  // now would be clipped — and a dismissed corner showing only the arrow has no room for
  // (or business showing) a marketing bubble anyway.
  const showTeaser =
    !state.open && !frameDocked && !!theme?.showMessagesOnLoad && !teaserDismissed;

  // Co-browse transparency: the visitor must SEE that an agent is viewing their
  // screen — surface a persistent banner with a Stop control, and pop the panel
  // open if it was collapsed when sharing began.
  const sharing = useSyncExternalStore(subscribeCobrowse, isCobrowsing, isCobrowsing);
  useEffect(() => {
    if (sharing) openPanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharing]);

  // Anonymous presence beat while the widget is loaded (live-visitors panel) — even when the
  // launcher stays closed; links to the conversation once a session exists. When an AGENT starts
  // the chat (Visitors panel → engage), the beat hands us the session: adopt it, hydrate the
  // agent's first message, auto-open the panel, and chime.
  // Only a LIVE session may ride the beat. The widget deliberately keeps a CLOSED session in
  // state so the rating screen stays reachable — but beating with that dead id linked the
  // presence row to a corpse and (server-side) used to block engage-adoption outright, so an
  // agent's "Start chat" never reached the visitor. Ended/closed => beat anonymously.
  useVisitorPresence(
    embed,
    state.phase === 'chatting' ? (state.session?.id ?? null) : null,
    (sid, token) => {
    if (!embed) return;
    visitorStore.setSessionToken(embed.cid, token);
    visitorStore.persistSessionId(embed.cid, sid);
    void resumeSession().then((resumed) => {
      if (resumed) {
        openPanel();
        playChime();
      }
    });
    },
  );

  // Queue position while nobody has picked the chat up ("you're #N in line"). The
  // session.claimed WS event clears claimedByAgentId's absence instantly, dropping the banner.
  const waitingForAgent =
    state.phase === 'chatting' && !!state.session && !state.session.claimedByAgentId;
  const queueSlot = useQueuePosition(embed?.cid ?? '', state.session?.id ?? null, waitingForAgent);

  // Voice: visitor can call the agent handling the chat, when the org has a softphone AND an agent
  // has claimed the session (someone to reach). Browser-to-browser WebRTC (see useVisitorCall).
  const call = useVisitorCall(embed?.cid ?? '', state.session?.id ?? null);
  // Only offer the call when an agent has claimed AND that agent is call-capable (has an assigned
  // Twilio number) — pushed on the session.claimed/assigned events. The server also enforces this
  // when minting the visitor's voice token (no agentIdentity → nothing to dial).
  const canCall =
    !!config?.voiceEnabled &&
    state.phase === 'chatting' &&
    !!state.session?.claimedByAgentId &&
    !!state.session?.agentCallable;

  // Proactive triggers: only while the visitor hasn't opened the widget AND no session is live
  // (a fresh pre-chat state — never mid-resume or an in-progress chat). On a match, surface the
  // greeting + auto-open the panel (one-shot). url_contains is matched against the loader-forwarded
  // host URL (embed.pageUrl), since document.referrer inside the iframe is origin-only.
  useTriggers(config?.triggers, {
    active: !state.open && !state.sessionToken && state.phase === 'prechat',
    hostUrl: embed?.pageUrl ?? null,
    onFire: (message) => {
      setProactive(message);
      autoOpenPanel();
    },
  });

  // Load public config + apply theme on mount, then attempt session resume.
  useEffect(() => {
    if (!embed) return;
    let cancelled = false;
    let teardownTheme: (() => void) | null = null;

    (async () => {
      try {
        const cfg = await loadWidgetConfig(embed.cid);
        if (cancelled) return;
        setConfig(cfg);
        teardownTheme = applyWidgetTheme(cfg);

        // Resume a persisted session if one exists; otherwise pick pre-chat vs
        // offline based on the config's live `online` flag. `resumeSession`
        // advances the phase to 'chatting' itself when it succeeds.
        visitorStore.setPhase('prechat');
        const resumed = await resumeSession();
        if (!cancelled && !resumed) {
          if (!cfg.online) {
            visitorStore.setPhase('offline');
          } else if (cfg.preChatEnabled === false) {
            // Pre-chat survey disabled → skip the form and open a session immediately.
            await startSession({});
          }
        }
      } catch {
        // TODO(phase 3): distinguish 404 (unknown widget) from network errors.
        if (!cancelled) {
          // Apply default theme so the error state is still branded/legible.
          teardownTheme = applyWidgetTheme(undefined);
          visitorStore.setError('Unable to load chat. Please try again later.');
        }
      }
    })();

    return () => {
      cancelled = true;
      teardownTheme?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embed]);

  // Apply org-level widget behaviors once the config (theme) is available:
  //   • sound gate (org can disable notification sound entirely)
  //   • launcher anchor (corner + edge spacing) — posted to the loader
  //   • auto-open the panel after the configured delay (one-shot)
  useEffect(() => {
    if (!theme) return;
    setSoundEnabled(theme.messageSound !== false);
    setMutedState(isMuted());
    postToLoader({
      type: 'position',
      corner: theme.launcherPosition === 'bottom-left' ? 'bottom-left' : 'bottom-right',
      side: theme.sideSpacing,
      bottom: theme.bottomSpacing,
    });

    if (theme.initiallyOpen && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      const delayMs = Math.max(0, (theme.timeToOpenSeconds ?? 0) * 1000);
      const t = window.setTimeout(autoOpenPanel, delayMs);
      return () => window.clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  // A dismissed launcher must never strand a LIVE conversation. If the agent (or the bot
  // still holding the chat) says something while the launcher is hidden, bring it back —
  // losing an in-flight conversation is worse than honoring the dismissal. The chime in
  // `useWidgetSocket` already fires on the same messages, so the bubble reappearing is not
  // the only signal. We restore the launcher but deliberately do NOT force the panel open:
  // taking over the customer's page uninvited is precisely what the visitor asked us not to do.
  //
  // `lastInboundIdRef` is seeded on the first render where the phase settles on 'chatting'
  // (which is also when a resume has finished rehydrating history), so replaying an existing
  // transcript on reload never counts as "a new message arrived".
  const lastInboundIdRef = useRef<string | null>(null);
  const inboundSeededRef = useRef(false);
  useEffect(() => {
    if (state.phase !== 'chatting') return;
    let latest: string | null = null;
    for (const m of state.messages) {
      if (m.senderType === 'agent' || m.senderType === 'bot') latest = m.id;
    }
    if (!inboundSeededRef.current) {
      inboundSeededRef.current = true;
      lastInboundIdRef.current = latest;
      return;
    }
    if (latest && latest !== lastInboundIdRef.current) {
      lastInboundIdRef.current = latest;
      if (dismissedRef.current) restoreLauncher();
    }
  }, [state.messages, state.phase, restoreLauncher]);

  // Start the show-delay clock once the config is known; release any auto-open that fired
  // while concealed the moment the icon appears (net: opens at max(openDelay, launcherDelay)).
  useEffect(() => {
    concealedRef.current = launcherConcealed;
  }, [launcherConcealed]);
  useEffect(() => {
    if (!config || launcherRevealed) return;
    const delayMs = Math.max(0, (theme?.launcherDelaySeconds ?? 0) * 1000);
    if (delayMs === 0) {
      setLauncherRevealed(true);
      return;
    }
    const t = window.setTimeout(() => {
      setLauncherRevealed(true);
      if (pendingAutoOpenRef.current && !dismissedRef.current) {
        pendingAutoOpenRef.current = false;
        setOpen(true);
      }
    }, delayMs);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, launcherRevealed]);

  // Notify the loader whenever the panel opens/closes so it resizes the iframe. When collapsed
  // with a greeting teaser, grow the collapsed frame so the bubble isn't clipped. When the
  // visitor has dismissed the launcher, the frame shrinks to the restore arrow (`dock`) —
  // never fully off the page, because the arrow is the visitor's only way back.
  //
  // `dock` also goes out while the handshake is unresolved (see `frameDocked`). Every loader
  // generation lands somewhere safe: the current one docks the frame around the arrow; the
  // hide-only build ignores `dock` and keeps the frame at its boot-time hidden state until the
  // handshake resolves `no` and a normal `collapse` follows; the pre-handshake build shows its
  // usual 96×96 frame, where the render below keeps a clickable arrow rather than dead space.
  useEffect(() => {
    if (launcherConcealed) return; // show-delay running — leave the frame at its boot-hidden state
    if (state.open) postToLoader({ type: 'expand' });
    else if (frameDocked) postToLoader({ type: 'dock' });
    else postToLoader({ type: 'collapse', teaser: showTeaser });
  }, [state.open, showTeaser, frameDocked, launcherConcealed]);

  // Keep the callback mirror on the EFFECTIVE state, not the stored flag. A dismissal the
  // loader cannot honor is inert everywhere or nowhere: leaving this on the raw flag would
  // silently swallow the org's auto-open and proactive triggers for a visitor who is looking
  // at a perfectly ordinary launcher (old loader, no × to explain why). The mutators below
  // still write it synchronously — a timer firing in the same tick as the tap must not miss it.
  useEffect(() => {
    dismissedRef.current = frameDocked;
  }, [frameDocked]);

  if (!embed) {
    return (
      <div className="p-4 text-sm text-red-600">
        Chat unavailable: missing or invalid embed id (?cid=ORG-WIDGET).
      </div>
    );
  }

  // Show-delay running: nothing widget-related exists on the page yet. The effect above left
  // the frame hidden; presence beats and the socket keep running underneath, so the Visitors
  // panel sees this visitor and an agent engage / session resume reveals instantly.
  if (launcherConcealed) return null;

  // Dismissed by the visitor: all that remains is the restore arrow. The effect above has
  // already asked the loader to shrink the frame around it, so the host page keeps its corner
  // back minus a 44px dock — and one tap on the arrow (or an agent replying) brings the
  // launcher back. Gated on `frameDocked`, not the stored flag: once the handshake settles on
  // a loader that cannot dock, the dismissal is inert and the full launcher renders instead —
  // there is never a state the visitor cannot see and click their way out of.
  if (!state.open && frameDocked) {
    const corner = theme?.launcherPosition === 'bottom-left' ? 'justify-start' : 'justify-end';
    return (
      <div className={`flex h-full w-full items-end p-1 ${corner}`}>
        <LauncherRestoreArrow onRestore={restoreLauncher} />
      </div>
    );
  }

  // Collapsed: only the launcher bubble is visible (optionally with a greeting teaser).
  if (!state.open) {
    const teaser = showTeaser
      ? config?.welcomeMessage?.trim() || 'Hi! How can we help?'
      : null;
    const corner = theme?.launcherPosition === 'bottom-left' ? 'justify-start' : 'justify-end';
    return (
      <div className={`flex h-full w-full items-end p-2 ${corner}`}>
        <LauncherBubble
          onOpen={() => setOpen(true)}
          theme={theme}
          teaser={teaser}
          onDismissTeaser={() => setTeaserDismissed(true)}
          // No × unless the loader has told us it can honor the dismissal. Withholding the
          // control is the whole compatibility story: a visitor can never reach a state the
          // loader on this page is unable to leave.
          onDismiss={canDismiss ? dismissLauncher : undefined}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-lc bg-lc-bg font-lc text-lc-text shadow-xl">
      {/* Header — org-branded color/gradient; renders the logo when configured. */}
      <header
        className="flex items-center justify-between px-3 py-2 text-lc-on-header"
        style={{
          backgroundImage:
            'linear-gradient(135deg, var(--lc-header), var(--lc-header-gradient))',
        }}
      >
        <span className="flex items-center gap-2 text-sm font-semibold">
          {theme?.logoUrl ? (
            <img
              src={theme.logoUrl}
              alt=""
              className="h-6 w-6 shrink-0 rounded object-contain"
              referrerPolicy="no-referrer"
            />
          ) : null}
          {theme?.headerName?.trim() || 'Chat with us'}
        </span>
        <div className="relative flex items-center gap-1">
          <button
            type="button"
            aria-label="Chat options"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            className="rounded px-1.5 py-1 text-lg leading-none hover:bg-black/10"
          >
            ⋮
          </button>
          {menuOpen ? (
            <>
              {/* click-away backdrop */}
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-8 z-20 min-w-[10rem] overflow-hidden rounded-lc bg-lc-bg text-lc-text shadow-xl ring-1 ring-black/10">
                {/* Call agent — only when the claiming agent is call-capable (has an assigned number). */}
                {canCall && call.phase === 'idle' ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      void call.start();
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-black/5"
                  >
                    <PhoneGlyph />
                    <span>Call agent</span>
                  </button>
                ) : null}
                {soundOn ? (
                  <button
                    type="button"
                    onClick={toggleMuted}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-black/5"
                  >
                    <span>Sound</span>
                    <span
                      className={`inline-flex h-4 w-7 items-center rounded-full px-0.5 transition-colors ${muted ? 'bg-black/20' : 'bg-lc-primary'}`}
                      aria-hidden
                    >
                      <span
                        className={`h-3 w-3 rounded-full bg-white transition-transform ${muted ? '' : 'translate-x-3'}`}
                      />
                    </span>
                  </button>
                ) : null}
                {state.phase === 'chatting' ? (
                  <button
                    type="button"
                    onClick={endChat}
                    className="w-full border-t border-black/10 px-3 py-2 text-left text-sm hover:bg-black/5"
                  >
                    End chat
                  </button>
                ) : null}
              </div>
            </>
          ) : null}
          <button
            type="button"
            aria-label="Minimize chat"
            onClick={() => setOpen(false)}
            className="rounded p-1 text-lg leading-none hover:bg-black/10"
          >
            ×
          </button>
        </div>
      </header>

      {/* Queue banner — the visitor can SEE they're in a moving line, not ignored. */}
      {waitingForAgent && queueSlot ? (
        <div className="flex items-center gap-2 border-b border-black/5 bg-lc-bubble-in px-3 py-2 text-sm text-lc-text" aria-live="polite">
          <span aria-hidden>⏳</span>
          <span>
            You're <span className="font-semibold">#{queueSlot.position}</span> in line — an agent
            will be with you in {formatWait(queueSlot.estimatedWaitSeconds)}.
          </span>
        </div>
      ) : null}

      {/* Co-browse consent banner — always visible while the agent views the page. */}
      {sharing ? (
        <div className="flex items-center justify-between gap-2 border-b border-black/5 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5" aria-hidden>
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
            </span>
            Agent is viewing this page
          </span>
          <button
            type="button"
            onClick={visitorStopCobrowse}
            className="rounded-full bg-red-500 px-3 py-1 text-xs font-medium text-white hover:opacity-90"
          >
            Stop sharing
          </button>
        </div>
      ) : null}

      {/* Live-call banner (visitor↔agent WebRTC — outbound and inbound). */}
      {call.phase !== 'idle' ? (
        <div className="flex items-center justify-between gap-2 border-b border-black/5 bg-lc-bubble-in px-3 py-2 text-sm text-lc-text">
          <span className="flex items-center gap-2">
            <PhoneGlyph
              className={
                call.phase === 'connecting' || call.phase === 'incoming' ? 'animate-pulse' : ''
              }
            />
            {call.phase === 'incoming'
              ? 'Agent is calling…'
              : call.phase === 'connecting'
                ? 'Calling agent…'
                : call.phase === 'active'
                  ? 'On call'
                  : call.phase === 'ended'
                    ? 'Call ended'
                    : (call.error ?? 'Call failed')}
          </span>
          <span className="flex items-center gap-2">
            {call.phase === 'incoming' ? (
              <button
                type="button"
                onClick={call.accept}
                className="rounded-full bg-lc-primary px-3 py-1 text-xs font-medium text-white hover:opacity-90"
              >
                Accept
              </button>
            ) : null}
            {call.phase === 'active' ? (
              <button
                type="button"
                onClick={call.toggleMute}
                className="rounded px-2 py-0.5 text-xs hover:bg-black/10"
              >
                {call.muted ? 'Unmute' : 'Mute'}
              </button>
            ) : null}
            {call.phase === 'connecting' || call.phase === 'active' || call.phase === 'incoming' ? (
              <button
                type="button"
                onClick={call.hangup}
                className="rounded-full bg-red-500 px-3 py-1 text-xs font-medium text-white hover:opacity-90"
              >
                {call.phase === 'incoming' ? 'Decline' : 'End'}
              </button>
            ) : (
              <button
                type="button"
                onClick={call.reset}
                className="rounded px-2 py-0.5 text-xs text-lc-text/60 hover:bg-black/10"
              >
                Dismiss
              </button>
            )}
          </span>
        </div>
      ) : null}

      {/* Body — chosen by phase. */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {renderPhase()}
      </div>

      {/* Branding footer (parity with the old "Powered by" strip) — org can hide it,
          show a company name, or show its logo. */}
      {theme?.showFooter !== false ? (
        <div className="flex items-center justify-center gap-1 border-t border-black/5 py-1.5 text-center text-[11px] text-lc-text/40">
          {theme?.footerMode === 'logo' && theme?.logoUrl ? (
            <img
              src={theme.logoUrl}
              alt=""
              className="h-3.5 object-contain"
              referrerPolicy="no-referrer"
            />
          ) : (
            <span>{theme?.footerCompanyName?.trim() || `⚡ ${widgetBrand.footerLabel}`}</span>
          )}
        </div>
      ) : null}
    </div>
  );

  function PhoneGlyph({ className = '' }: { className?: string }) {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`inline-block h-[1.1rem] w-[1.1rem] ${className}`}
        aria-hidden
      >
        <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.9.6 2.8.7a2 2 0 0 1 1.7 2z" />
      </svg>
    );
  }

  function renderPhase() {
    switch (state.phase) {
      case 'loading':
        return (
          <div className="flex flex-1 items-center justify-center text-sm text-lc-text/50">
            Loading…
          </div>
        );
      case 'prechat':
        return <PreChatForm config={config} onSubmit={startSession} proactiveMessage={proactive} />;
      case 'chatting':
        return (
          <ChatScreen
            messages={state.messages}
            connectionState={connectionState}
            onSend={(body) => {
              stopVisitorTyping(); // the message itself supersedes the typing signal
              sendMessage(body);
            }}
            onAttach={sendAttachment}
            onTyping={signalVisitorTyping}
            agentTyping={state.agentTyping}
            theme={theme}
            // Prefer the proactive trigger greeting (if one fired) so it still shows as the
            // empty-state bubble after a no-fields pre-chat auto-starts the session.
            welcomeMessage={proactive ?? config?.welcomeMessage}
          />
        );
      case 'offline':
        return <OfflineScreen config={config} cid={embed?.cid ?? ''} />;
      case 'ended':
        return (
          <EndedScreen
            onRestart={() => {
              // `resumeSession` now deliberately KEEPS the ended session's token/id around (so a
              // reload can still reach the CSAT screen — see useVisitorSession) — so starting a
              // truly NEW chat must explicitly drop that old session here first, or the reload
              // below would just resume straight back into the same 'ended' screen.
              if (embed) visitorStore.setSessionToken(embed.cid, null);
              window.location.reload();
            }}
            dualRating={config?.dualRatingEnabled ?? false}
            endText={theme?.agentEndText}
            allowRestart={theme?.restartAgentFlow !== false}
          />
        );
      case 'error':
        return (
          <div className="flex flex-1 items-center justify-center p-4 text-center text-sm text-lc-text/70">
            {state.error ?? 'Something went wrong.'}
          </div>
        );
      default:
        return null;
    }
  }
}
